import { toast } from 'sonner';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { Badge } from '@/components/ui/Badge';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Tooltip } from '@/components/ui/Tooltip';
import { openUntrustedUrl } from '@/utils/external-links';
import { handleExternalUrlClick } from '@/utils/platform';

import { WIKI_PROVIDERS, type WikiAccount } from '../people-wiki.types';

import { ProviderIcon } from './provider-icon';

const BADGE_CLASS_NAME =
  'gap-1 whitespace-nowrap font-normal text-foreground-muted';

const isProfileLink = ({ source }: WikiAccount): boolean => source !== 'sync';

const copyEmail = async (email: string) => {
  await navigator.clipboard.writeText(email);
  toast.success('Email copied');
};

/** `onRemove` is given for an account that can be removed, and it adds a menu on hover. */
export const AccountBadge = ({
  account,
  onRemove,
}: {
  account: WikiAccount;
  onRemove?: () => void;
}) => {
  const { href } = account;
  const hasMenu = onRemove !== undefined;
  const isEmail = account.provider === WIKI_PROVIDERS.email;

  const badge = (
    <Badge variant="secondary" className={BADGE_CLASS_NAME}>
      <ProviderIcon provider={account.provider} />
      {account.label}
      {account.source === 'agent' && (
        // The tooltip of the badge says who added it, so the mark has none of its own
        <span className="inline-flex opacity-60">
          <AppLogo size={10} customColor="currentColor" decorative />
        </span>
      )}
    </Badge>
  );

  return (
    <span className="group/badge inline-flex items-center gap-0.5">
      <Tooltip
        label={isEmail ? `${account.detail} · Click to copy` : account.detail}
        side="top"
        delayDuration={200}
      >
        {href === null ? (
          <span className="cursor-default">{badge}</span>
        ) : (
          <button
            type="button"
            className="cursor-pointer rounded-md focus-ring"
            onClick={(event) => {
              event.stopPropagation();
              // A profile that someone typed or a lookup found is not ours to trust
              if (isEmail) {
                void copyEmail(account.label);
              } else if (isProfileLink(account)) {
                openUntrustedUrl(href);
              } else {
                handleExternalUrlClick(href);
              }
            }}
          >
            {badge}
          </button>
        )}
      </Tooltip>
      {hasMenu && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            variant="ghost"
            aria-label={`Actions for ${account.label}`}
            // Always in the row while hidden, so showing it never moves "+ Add"
            className="focus-ring-inset size-5 shrink-0 justify-center rounded-md p-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/badge:opacity-100 group-has-[:focus-visible]/badge:opacity-100 data-[state=open]:opacity-100"
          >
            <Icons.moreHorizontal className="size-3" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start">
            <DropdownMenu.ItemWithConfirm onConfirm={onRemove}>
              {account.source === 'web'
                ? 'Remove, wrong profile'
                : 'Remove account'}
            </DropdownMenu.ItemWithConfirm>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
    </span>
  );
};
